import * as React from "react";
import {
  Grid,
  Link,
  TextField,
  Select,
  Box,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
} from "@mui/material";
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext, ImagesContext } from "../Context";
import { stateOptions } from "../patients/AddPatientManual";
import OrganizationShow from "./OrganizationShow";
import AddOrganizationModal from "./AddOrganizationModal";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

interface Props {
  csrfToken: string;
  menu_track_src: string;
  user_id: number;
  sort_ascending_src: string;
  sort_descending_src: string;
  sort_plain_src: string;
  pencil_grey: string;
}

const OrganizationsIndex: React.FC<Props> = (props: any) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const images = React.useContext(ImagesContext);

  const [sortObject, setSortObject] = React.useState<any>({
    field: "name",
    direction: "ascending",
  });
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageCount, setPageCount] = React.useState<number>(0);
  const [pageLinks, setPageLinks] = React.useState<any>([]);
  const [paginatedList, setPaginatedList] = React.useState<any>([]);
  const perPage = 20;
  const [customers, setCustomers] = React.useState<any>([]);
  const [fullCustomers, setFullCustomers] = React.useState<any>([]);
  const [selectedOrganization, setSelectedOrganization] =
    React.useState<any>(null);
  const afterFetch = React.useRef(false);
  const [nameSearch, setNameSearch] = React.useState<string>("");
  const [citySearch, setCitySearch] = React.useState<string>("");
  const [stateSearch, setStateSearch] = React.useState<string>("");
  const [showAddOrganizationModal, setShowAddOrganizationModal] =
    React.useState<boolean>(false);
  const [isEditing, setIsEditing] = React.useState<boolean>(false);
  const [placeOfServiceList, setPlaceOfServiceList] = React.useState<any>([]);
  const [refreshOrganizations, setRefreshOrganizations] = React.useState(false);
  const [placeOfService, setPlaceOfService] = React.useState("");

  const getOrganizations = () => {
    fetch(
      `/data_fetching/customers?name=${nameSearch}&city=${citySearch}&state=${stateSearch}`,
      {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      }
    )
      .then((result) => result.json())
      .then((result) => {
        if (typeof result.error !== "undefined") {
          console.log(result.error);
        } else {
          setCustomers(result.customers);
          setFullCustomers(result.customers);
          setPlaceOfServiceList(result.place_of_service_list);
          const pagList = paginateList(result.customers);
          setPaginatedList(pagList);
          if (pagList.length >= 1) {
            setSelectedOrganization(pagList[0]);
          }
        }
        afterFetch.current = true;
      })
      .catch((error) => {
        console.log(error);
      });
  };

  React.useEffect(() => {
    getOrganizations();
  }, [nameSearch, citySearch, stateSearch, refreshOrganizations]);

  const setSortOrder = (sortBy, direction) => {
    let sort = {
      field: sortBy,
      direction: direction,
    };
    setSortObject(sort);
  };

  const paginateList = (list) => {
    if (list.length == 0) {
      return [];
    } else {
      let customersList = list;
      if (currentPage == 1) {
        return customersList.slice(0, perPage);
      } else {
        let offsetIndex = perPage * (currentPage - 1);
        return customersList.slice(offsetIndex, offsetIndex + perPage);
      }
    }
  };

  const updatePagination = () => {
    setPaginatedList(paginateList(customers));
  };

  React.useEffect(() => {
    sortList();
  }, [sortObject, fullCustomers, refreshOrganizations]);

  React.useEffect(() => {
    if (customers) {
      let pages = customers.length / perPage;
      setPageCount(Math.ceil(pages));
      const pageLinks = [];
      for (let i = 1; i <= pages + 1; i++) {
        pageLinks.push(
          <Link
            key={i}
            onClick={() => {
              selectPage(i);
            }}
            className={currentPage == i ? "linkDark" : "linkLight"}
          >
            {i}
          </Link>
        );
      }
      setPageLinks(pageLinks);
    }
  }, [customers, currentPage]);

  React.useEffect(() => {
    basicSearch();
  }, [nameSearch]);

  const getFilteredList = (customerListParams) => {
    let customerList = customerListParams.filter((customer) => {
      const matchesName = nameSearch
        ? customer.name?.toLowerCase().includes(nameSearch.toLowerCase())
        : true;
      const matchesCity = citySearch
        ? customer.city?.toLowerCase().includes(citySearch.toLowerCase())
        : true;
      return matchesName && matchesCity;
    });
    return customerList;
  };

  const basicSearch = () => {
    if (fullCustomers) {
      let customersList = getFilteredList(fullCustomers);
      setCustomers(customersList);
      setCurrentPage(1);
    }
  };

  React.useEffect(() => {
    updatePagination();
  }, [customers, currentPage]);

  const selectPage = (page) => {
    setCurrentPage(page);
  };

  const sortList = () => {
    let customersList = [].concat(customers);

    customersList.sort((a, b) => (a.id > b.id ? 1 : -1));

    if (sortObject.field == "name") {
      customersList.sort((a, b) =>
        a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1
      );
    }

    if (sortObject.field == "address") {
      customersList.sort((a, b) =>
        a.address?.toLowerCase() > b.address?.toLowerCase() ? 1 : -1
      );
    }

    if (sortObject.field == "phone") {
      customersList.sort((a, b) =>
        a["phone_number"] > b["phone_number"] ? 1 : -1
      );
    }

    if (sortObject.direction == "descending") {
      customersList.reverse();
    }

    const searchedCustomerList = getFilteredList(customersList);
    setCustomers(searchedCustomerList);
    if (!afterFetch.current) {
      setSelectedOrganization(customersList[0]);
    }
    setCurrentPage(1);
  };

  const handleEditingOn = (event) => {
    event.preventDefault();
    setIsEditing(true);
    setShowAddOrganizationModal(true);
    setRefreshOrganizations(false);
  };

  const openAddOrganizationModal = () => {
    setShowAddOrganizationModal(true);
    setRefreshOrganizations(false);
  };

  React.useEffect(() => {
    setPlaceOfService(selectedOrganization?.place_of_service_code);
  }, [selectedOrganization]);

  return (
    <div className="coreTeam">
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        className="container2"
      >
        <Grid item xs={6} className="patient-index-container">
          <Grid container className="patient-search-container">
            <Grid container>
              <Grid item xs={12}>
                <p className="search-form-label">ORGANIZATION SEARCH</p>
              </Grid>
            </Grid>
            <Grid container justifyContent="space-between">
              <Grid item xs={12} sx={{ marginRight: "30px" }}>
                <Grid container justifyContent="space-between" spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      id="nameSearch"
                      className="textInput plainLabel"
                      label="Name"
                      value={nameSearch}
                      onChange={(event) => {
                        setNameSearch(event.target.value);
                      }}
                      variant="filled"
                      size="small"
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      id="citySearch"
                      className="textInput plainLabel"
                      label="City"
                      value={citySearch}
                      onChange={(event) => {
                        setCitySearch(event.target.value);
                      }}
                      variant="filled"
                      size="small"
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Select
                      id="stateSearch"
                      value={stateSearch}
                      required
                      variant="filled"
                      className="textInput plainLabel"
                      sx={{
                        backgroundColor: "#fcf6f4 !important",
                        height: "45.13px",
                        "&:before, &:after": {
                          borderBottom: "none !important",
                        },
                        "&:hover:not(.Mui-disabled):before": {
                          borderBottom: "none !important",
                        },
                        "& .MuiSelect-filled": {
                          paddingTop: "15px",
                          paddingBottom: "10px",
                        },
                      }}
                      inputProps={{
                        style: { paddingTop: "15px", paddingBottom: "10px" },
                      }}
                      onChange={(e) => {
                        setStateSearch(e.target.value);
                      }}
                      native
                    >
                      <option value={""}>Select State</option>
                      {stateOptions}
                    </Select>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            container
            className="panel-container"
            borderRadius={"4px"}
            style={{ backgroundColor: "transparent", marginTop: "0px" }}
          >
            <Grid item xs={12} style={{ marginRight: "30px" }}>
              <Grid container className="panel-show-container">
                <Grid container className="panel-information-container">
                  <Grid container direction="row" className="admin-header">
                    <Grid item xs={12} className="box-header">
                      <Stack
                        direction={"row"}
                        justifyContent={"space-between"}
                        paddingX={3}
                        paddingY={1}
                        alignItems={"center"}
                        display={"flex"}
                      >
                        <Grid item>
                          <Stack direction="row" columnGap="12px" alignItems="center">
                            <Link
                              style={{
                                textDecoration: "none",
                                display: "flex",
                                alignItems: "center",
                              }}
                              href={`/providers-and-organizations`}
                            >
                              <ArrowBackIosNewIcon
                                style={{
                                  borderRadius: "3px",
                                  padding: "8px",
                                  color: "black",
                                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  transition: "all 0.3s ease",
                                  fontSize: "14px",
                                }}
                              />
                            </Link>
                            <p className="panel-header">Organizations List</p>
                          </Stack>
                        </Grid>
                        <Link
                          className="action-link row"
                          style={{ backgroundColor: "transparent" }}
                          onClick={openAddOrganizationModal}
                        >
                          <img
                            src={images.add_icon}
                            alt="Add Charge"
                            className="action-icon-image"
                            style={{ width: 20, maxHeight: 20 }}
                          />
                          <span className="app-user-text disable-pointer">
                            Add Organization
                          </span>
                        </Link>
                      </Stack>
                    </Grid>
                  </Grid>
                  <Grid container className="general-patient-info-container">
                    <Grid
                      item
                      xs={12}
                      className="info-container"
                      style={{ padding: "0px" }}
                    >
                      <TableContainer>
                        <Table>
                          <TableHead className="table-header-box">
                            <TableRow>
                              <TableCell
                                align="left"
                                colSpan={2}
                                onClick={() => {
                                  setSortOrder(
                                    "name",
                                    sortObject.direction == "ascending"
                                      ? "descending"
                                      : "ascending"
                                  );
                                }}
                                className="table-header-cell"
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <strong
                                    style={{ fontFamily: "QuicksandMedium" }}
                                  >
                                    Organization ({customers?.length})
                                  </strong>

                                  {sortObject.field == "last_name" ? (
                                    <span className="sortIndicator provider">
                                      {sortObject.direction == "ascending" ? (
                                        <img
                                          src={props.sort_ascending_src}
                                          width="10"
                                          className="sort-icon"
                                          alt="Sort Asc"
                                        />
                                      ) : (
                                        <img
                                          src={props.sort_descending_src}
                                          width="10"
                                          className="sort-icon"
                                          alt="Sort Desc"
                                        />
                                      )}
                                    </span>
                                  ) : (
                                    <span className="sortIndicator provider">
                                      <img
                                        src={props.sort_plain_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Asc"
                                      />
                                    </span>
                                  )}
                                </Box>
                              </TableCell>

                              <TableCell
                                align="left"
                                onClick={() => {
                                  setSortOrder(
                                    "address",
                                    sortObject.direction == "ascending"
                                      ? "descending"
                                      : "ascending"
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <strong
                                  style={{ fontFamily: "QuicksandMedium" }}
                                >
                                  Address
                                </strong>
                                {sortObject.field == "address" ? (
                                  <span className="sortIndicator">
                                    {sortObject.direction == "ascending" ? (
                                      <img
                                        src={props.sort_ascending_src}
                                        width="10"
                                        alt="Sort Asc"
                                      />
                                    ) : (
                                      <img
                                        src={props.sort_descending_src}
                                        width="10"
                                        alt="Sort Desc"
                                      />
                                    )}
                                  </span>
                                ) : (
                                  <span className="sortIndicator">
                                    <img
                                      src={props.sort_plain_src}
                                      width="10"
                                      className="sort-icon"
                                      alt="Sort Asc"
                                    />
                                  </span>
                                )}
                              </TableCell>
                              <TableCell
                                align="left"
                                onClick={() => {
                                  setSortOrder(
                                    "phone",
                                    sortObject.direction == "ascending"
                                      ? "descending"
                                      : "ascending"
                                  );
                                }}
                                style={{ cursor: "pointer", width: "95px" }}
                              >
                                <strong
                                  style={{ fontFamily: "QuicksandMedium" }}
                                >
                                  Phone
                                </strong>
                                {sortObject.field == "phone" ? (
                                  <span className="sortIndicator">
                                    {sortObject.direction == "ascending" ? (
                                      <img
                                        src={props.sort_ascending_src}
                                        width="10"
                                        alt="Sort Asc"
                                      />
                                    ) : (
                                      <img
                                        src={props.sort_descending_src}
                                        width="10"
                                        alt="Sort Desc"
                                      />
                                    )}
                                  </span>
                                ) : (
                                  <span className="sortIndicator">
                                    <img
                                      src={props.sort_plain_src}
                                      width="10"
                                      className="sort-icon"
                                      alt="Sort Asc"
                                    />
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          {paginatedList ? (
                            <TableBody>
                              {paginatedList.map((customer, index) => (
                                <TableRow
                                  key={index + 1}
                                  sx={{
                                    "& td, & th": { borderBottom: "none" },
                                  }}
                                >
                                  <TableCell style={{ padding: "10px" }}>
                                    <input
                                      type="radio"
                                      name="selectCustomer"
                                      onChange={() => {
                                        setSelectedOrganization(customer);
                                      }}
                                      checked={
                                        selectedOrganization?.id == customer.id
                                      }
                                    />
                                  </TableCell>
                                  <TableCell
                                    size="small"
                                    scope="row"
                                    align="left"
                                    className="tableCell"
                                  >
                                    {customer.name}
                                  </TableCell>
                                  <TableCell
                                    size="small"
                                    scope="row"
                                    align="left"
                                    className="tableCell"
                                  >
                                    {customer.address
                                      ? customer.full_address
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    size="small"
                                    scope="row"
                                    align="left"
                                    className="tableCell"
                                  >
                                    {customer.phone_number}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          ) : (
                            afterFetch.current && <p>No users found.</p>
                          )}
                        </Table>
                      </TableContainer>
                      <p className="pagerLinks">
                        <Link
                          onClick={() => {
                            selectPage(
                              currentPage > 1 ? currentPage - 1 : currentPage
                            );
                          }}
                          className="linkDark"
                        >
                          &lt;
                        </Link>
                        {pageLinks}
                        <Link
                          onClick={() => {
                            selectPage(
                              currentPage < pageCount
                                ? currentPage + 1
                                : currentPage
                            );
                          }}
                          className="linkDark"
                        >
                          &gt;
                        </Link>
                      </p>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {selectedOrganization && (
          <OrganizationShow
            pencil_grey={props.pencil_grey}
            organization={selectedOrganization}
            handleEditingOn={handleEditingOn}
            placeOfServiceList={placeOfServiceList}
            placeOfService={placeOfService}
            setPlaceOfService={setPlaceOfService}
            setSelectedOrganization={setSelectedOrganization}
          />
        )}

        {showAddOrganizationModal && (
          <AddOrganizationModal
            selectedOrganization={isEditing ? selectedOrganization : null}
            showAddOrganizationModal={showAddOrganizationModal}
            setShowAddOrganizationModal={setShowAddOrganizationModal}
            setIsEditing={setIsEditing}
            placeOfServiceList={placeOfServiceList}
            setRefreshOrganizations={setRefreshOrganizations}
            setSelectedOrganization={setSelectedOrganization}
            updateOrganizationInList={(updatedOrg) => {
              setCustomers(
                customers.map((org) =>
                  org.id === updatedOrg.id ? updatedOrg : org
                )
              );
            }}
          />
        )}
      </Grid>
    </div>
  );
};

export default OrganizationsIndex;
